import { useIntl } from 'react-intl';

import type { CreditAllowanceResponse } from '../../../contracts/types/credit-allowance.types';
import {
  getCreditAllowanceNudgeVariant,
  isComplimentaryCreditAllowance,
} from '../../../data/query/credit-allowance.utils';
import { useQueryUserBillingInfo } from '../../../data/query/useQueryUserBillingInfo';
import { isMaxBillingSeat } from '../../settings/is-max-billing-seat';
import { formatCreditAllowanceResetCaption } from './format-credit-allowance-reset-label';
import {
  resolveUsageNudgeCell,
  resolveUsageNudgeCta,
  resolveUsageNudgeThreshold,
  resolveUsageNudgeTrigger,
  type UsageNudgeCell,
  type UsageNudgeCta,
  type UsageNudgeThreshold,
  type UsageNudgeTrigger,
} from './resolve-usage-nudge';
import { usageNudgeCardMessages } from './usage-nudge-card-messages';
import { useUsageNudgeTeamRole } from './use-usage-nudge-team-role';

export type SideNavUsageNudgeCta = UsageNudgeCta & {
  label: string;
  icon: 'standard' | 'practice';
};

export type SideNavUsageNudgeCard = {
  title: string;
  body: string;
};

export type SideNavUsageNudge = {
  cell: UsageNudgeCell | null;
  threshold: UsageNudgeThreshold | null;
  trigger: UsageNudgeTrigger;
  /** Where the card's button goes, whether or not a card is due. */
  cta: SideNavUsageNudgeCta | null;
  card: SideNavUsageNudgeCard | null;
};

/** Standard seats are pitched from Free and trials; everything else sells Max or Practice. */
const ctaIconForCell = (cell: UsageNudgeCell): 'standard' | 'practice' =>
  cell === 'trial' || cell === 'free' || cell === 'clinicianStandard'
    ? 'standard'
    : 'practice';

export type UseSideNavUsageNudgeArgs = {
  /** `release-growth-heidi-2-usage-nudges` AND the Heidi 2 opt-in credit usage surface. */
  isEnabled: boolean;
  allowance: CreditAllowanceResponse | undefined;
  isTrialing: boolean;
  isTeamUpgradesEnabled: boolean;
  /** App-resolved: the member may send their admin a seat upgrade request. */
  canRequestTeamSeatUpgrade: boolean;
  isAppleCompliantBuild: boolean;
};

const DISABLED: SideNavUsageNudge = {
  cell: null,
  threshold: null,
  trigger: { forceTile: false },
  cta: null,
  card: null,
};

/** The sidebar usage button's place in the upgrade-nudge matrix. */
export function useSideNavUsageNudge({
  isEnabled,
  allowance,
  isTrialing,
  isTeamUpgradesEnabled,
  canRequestTeamSeatUpgrade,
  isAppleCompliantBuild,
}: UseSideNavUsageNudgeArgs): SideNavUsageNudge {
  const intl = useIntl();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { hasTeam, isTeamAdmin, isRoleForced } = useUsageNudgeTeamRole();

  if (!isEnabled || allowance == null) return DISABLED;

  const cell = resolveUsageNudgeCell({
    tier: billingInfo?.new_product_tier,
    isMaxSeat: isMaxBillingSeat(billingInfo),
    isTrialing,
    isComplimentaryAllowance: isComplimentaryCreditAllowance(allowance),
    isTeamAdmin,
  });
  if (cell == null) return DISABLED;

  const threshold = resolveUsageNudgeThreshold(getCreditAllowanceNudgeVariant(allowance));
  const resolvedCta = isAppleCompliantBuild
    ? null
    : resolveUsageNudgeCta(cell, {
        hasTeam,
        isTeamUpgradesEnabled,
        isRequestUpgradeAvailable: canRequestTeamSeatUpgrade || isRoleForced,
      });
  const cta =
    resolvedCta == null
      ? null
      : {
          ...resolvedCta,
          label: intl.formatMessage(
            resolvedCta.kind === 'requestUpgrade'
              ? usageNudgeCardMessages.requestUpgrade
              : usageNudgeCardMessages.upgrade
          ),
          icon: ctaIconForCell(cell),
        };

  return {
    cell,
    threshold,
    trigger: resolveUsageNudgeTrigger(cell, threshold),
    cta,
    card:
      threshold == null
        ? null
        : {
            title: intl.formatMessage(
              threshold === 100
                ? usageNudgeCardMessages.reachedTitle
                : usageNudgeCardMessages.almostReachedTitle
            ),
            body: intl.formatMessage(usageNudgeCardMessages.limitResets, {
              date: formatCreditAllowanceResetCaption(intl, allowance.cycle_end).date,
            }),
          },
  };
}
